import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, ArrowRight, Calendar, Tag, FileText, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useSocialMeta } from '../lib/useSocialMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const i18n = {
  en: {
    backToSite: 'Back to Site',
    title: 'Skopelos Journal',
    subtitle: 'Insights about buying property in Skopelos, life on the island, and our cottages.',
    readMore: 'Read more',
    empty: 'No articles published yet. Check back soon — we\'re publishing fresh insights about Skopelos and the buying process regularly.',
    loadError: 'Could not load articles right now. Please try again shortly.',
    minRead: 'min read',
  },
};

function readingTime(text) {
  if (!text) return 1;
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

const Blog = () => {
  const { language } = useLanguage();
  const t = i18n[language] || i18n.en;
  const navigate = useNavigate();

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useSocialMeta({
    title: 'Skopelos Journal — Property insights & buyers\' guides',
    description: 'Insights about buying property in Skopelos, life on the island, and our traditional cottages.',
    image: 'https://customer-assets.emergentagent.com/job_8e4dc794-01c5-4aa3-adec-b02ef8fc9fa7/artifacts/mki6abqp_dji_fly_20240529_065404_449_1716954893598_pano_optimized.jpg',
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    type: 'website',
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/articles?language=${language}&limit=50`);
        if (!res.ok) throw new Error('load failed');
        const data = await res.json();
        // If empty for selected language, fall back to English
        if (data.length === 0 && language !== 'en') {
          const fb = await fetch(`${API_URL}/api/articles?language=en&limit=50`);
          if (fb.ok) {
            setArticles(await fb.json());
            return;
          }
        }
        setArticles(data);
      } catch (e) {
        setError(t.loadError);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [language, t.loadError]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-800"
            data-testid="blog-back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backToSite}
          </Button>
          <h2 className="font-serif text-lg text-slate-800 hidden md:block">Skopelos Journal</h2>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-10">
          <h1 className="font-serif text-4xl md:text-5xl text-slate-800 mb-3" data-testid="blog-title">
            {t.title}
          </h1>
          <p className="text-slate-600 max-w-2xl leading-relaxed">{t.subtitle}</p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && articles.length === 0 && (
          <Card className="border-none shadow-md bg-white">
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 max-w-md mx-auto">{t.empty}</p>
            </CardContent>
          </Card>
        )}

        {!loading && !error && articles.length > 0 && (
          <div className="grid gap-6 md:grid-cols-2" data-testid="blog-list">
            {articles.map((a) => (
              <Link
                to={`/blog/${a.slug}`}
                key={a.id}
                data-testid={`blog-card-${a.slug}`}
                className="group block"
              >
                <article className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                  {a.hero_image_url && (
                    <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                      <img
                        src={a.hero_image_url}
                        alt={a.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(a.published_at || a.created_at)}
                      </span>
                      <span>·</span>
                      <span>{readingTime(a.content)} {t.minRead}</span>
                    </div>
                    <h2 className="font-serif text-xl text-slate-800 mb-2 leading-tight group-hover:text-[#d4845f] transition-colors">
                      {a.title}
                    </h2>
                    {a.excerpt && (
                      <p className="text-sm text-slate-600 leading-relaxed mb-4 flex-1">
                        {a.excerpt}
                      </p>
                    )}
                    {a.tags && a.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {a.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="text-[10px] uppercase tracking-wide px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full inline-flex items-center gap-1"
                          >
                            <Tag className="w-2.5 h-2.5" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <span className="text-sm font-medium text-[#d4845f] inline-flex items-center gap-1.5 mt-auto">
                      {t.readMore}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Blog;
